import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for safely managing blob URLs with automatic cleanup
 * @param {File|null} file - The file to create a blob URL for
 * @returns {string|null} - The blob URL or null if no file
 */
export const useBlobUrl = (file) => {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!file || !(file instanceof File)) {
      setBlobUrl(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setBlobUrl(url);

    // Cleanup function to revoke the blob URL
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  return blobUrl;
};

/**
 * Hook for managing file upload with preview
 * @returns {object} - File management utilities
 */
export const useFileUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  const blobUrl = useBlobUrl(selectedFile);

  const handleFileSelect = useCallback((file) => {
    if (!file || !(file instanceof File)) {
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    // Validate file type for images
    if (file.type.startsWith('image/')) {
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
      setPreviewUrl(null);
    }
  }, []);

  const clearFile = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
  }, []);

  // Update preview URL when blob URL changes
  useEffect(() => {
    setPreviewUrl(blobUrl);
  }, [blobUrl]);

  return {
    selectedFile,
    previewUrl,
    handleFileSelect,
    clearFile,
    hasFile: !!selectedFile
  };
};